import React, { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import useLocales from 'hooks/useLocales';
import { ILocation } from 'core/interface/work-experience-side';
import { RootState } from 'redux/store';
import { JobService } from 'services/jobs/job';
import { useDebounce } from 'helpers/useDebounce';
import {
  EditableShell,
  useInlineEditHint
} from 'components/companyJobDetail/inlineFields';

type InlineLocationsProps = {
  locations: Array<ILocation | number>;
  disabled?: boolean;
  onSave: (locations: ILocation[]) => Promise<boolean>;
};

const resolveLocations = (locations: Array<ILocation | number>): ILocation[] =>
  locations
    .map((location) => {
      if (typeof location === 'object' && location?.id) {
        return {
          id: location.id,
          display_name: location.display_name || ''
        };
      }
      return null;
    })
    .filter((item): item is ILocation => Boolean(item?.id && item.display_name));

const InlineLocations: FC<InlineLocationsProps> = ({
  locations,
  disabled,
  onSave
}) => {
  const { t } = useLocales();
  const hint = useInlineEditHint();
  const cities = useSelector(
    (state: RootState) => state.locations?.cities || []
  );
  const resolved = useMemo(
    () => resolveLocations(locations || []),
    [locations]
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ILocation[]>(resolved);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 400);

  useEffect(() => {
    if (!editing) {
      setDraft(resolved);
      setQuery('');
      setListOpen(false);
    }
  }, [resolved, editing]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    JobService.getLocations({ limit: 50, q: debouncedQuery });
  }, [editing, debouncedQuery]);

  const options = useMemo(() => {
    const selectedIds = new Set(draft.map((item) => item.id));
    const normalizedQuery = query.trim().toLowerCase();

    return (Array.isArray(cities) ? cities : [])
      .filter((city) => {
        if (selectedIds.has(city.id)) {
          return false;
        }
        if (!normalizedQuery) {
          return true;
        }
        return city.display_name.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 50)
      .map((city) => ({
        id: city.id,
        display_name: city.display_name
      }));
  }, [cities, draft, query]);

  const handleSave = async () => {
    if (!draft.length) {
      return;
    }
    const ok = await onSave(draft);
    if (ok) {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="company-job-detail__location-editor is-editing">
        <div className="company-job-detail__location-editor-input">
          <Autocomplete
            multiple
            fullWidth
            disableClearable
            open={listOpen}
            onOpen={() => setListOpen(true)}
            onClose={() => setListOpen(false)}
            className="company-job-detail__location-autocomplete"
            options={options}
            value={draft}
            inputValue={query}
            getOptionLabel={(option) => option.display_name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(items) => items}
            onChange={(_, next, reason) => {
              setDraft(next);
              if (reason === 'selectOption') {
                setQuery('');
                setListOpen(false);
              }
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'reset') {
                return;
              }
              setQuery(value);
              if (reason === 'input') {
                setListOpen(true);
              }
            }}
            componentsProps={{
              popper: {
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'offset',
                    options: { offset: [0, 6] }
                  }
                ]
              },
              paper: {
                sx: { mt: 0.5 }
              }
            }}
            ListboxProps={{ style: { maxHeight: 220 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                placeholder={
                  draft.length
                    ? t('Add another location')
                    : t('Country, province, city')
                }
                helperText={
                  draft.length
                    ? undefined
                    : t('At least one location is required')
                }
              />
            )}
          />
        </div>
        <div className="company-job-detail__edit-actions">
          <Button size="small" onClick={() => setEditing(false)}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !draft.length}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
    >
      {resolved.length ? (
        <span>{resolved.map((item) => item.display_name).join(', ')}</span>
      ) : (
        <span className="company-job-detail__placeholder">{hint}</span>
      )}
    </EditableShell>
  );
};

export default InlineLocations;
